import '../App.css';
import React, { useContext, useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import NavBar from '../components/NavBar';
import LessonCreatorSidebar from '../components/LessonCreatorSidebar';
import { FileContext } from '../components/FileContext';
import ElementSelector from '../components/ElementSelector';
import ProtectedRoute from '../components/ProtectedRoute';
import { useFeedback } from '../contexts/FeedbackContext';
import { useLanguage } from '../contexts/LanguageContext';
import { t } from '../utils/translations';
import { track } from '../utils/analytics';
import { invalidateDashboardPlans } from '../utils/dashboardInvalidation';
import styles from './SelectElements.module.css';

function SelectElementsContent() {
  const location = useLocation();
  const navigate = useNavigate();
  const { files } = useContext(FileContext);
  const { openFeedbackPanel } = useFeedback();
  const { language } = useLanguage();

  const handleFeedback = () => {
    openFeedbackPanel('screen', {
      page_id: 'select-elements',
      page_name: 'Select Elements',
      page_elements: [
        'Cultural element cards',
        'Story selection',
        'Proverb selection',
        'Cultural practice selection',
        'Generate button',
        'Next button',
      ],
    });
  };

  const {
    selectedSubject,
    selectedGrade,
    selectedConcept,
    selectedSubconcept,
    selectedStandardIdentifier,
    selectedStandardDescription,
    selectedLanguage,
    selectedMoku,
    selectedIsland,
    files: incomingFiles,
    fileTypes,
    resumeData,
    isResuming,
  } = location.state || {};
  
  const [selectedElements, setSelectedElements] = useState(() => {
    const fromState = location.state?.selectedElements;
    if (fromState && typeof fromState === 'object') {
      return {
        story: Boolean(fromState.story),
        proverb: Boolean(fromState.proverb),
        cultural_practice: Boolean(fromState.cultural_practice),
      };
    }
    return {
      story: false,
      proverb: false,
      cultural_practice: false,
    };
  });
  
  const [generatedElements, setGeneratedElements] = useState(
    isResuming && resumeData?.lesson_plan_data?.generatedElements 
      ? resumeData.lesson_plan_data.generatedElements 
      : location.state?.generatedElements || {}
  );
  
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  }, []);
  
  const handleSelectionChange = (updatedSelection) => {
    setSelectedElements(updatedSelection);
  };
  
  const handleNext = async () => {
    const hasSelection = Object.values(selectedElements).some(val => val);
    if (!hasSelection) {
      alert("Please select at least one cultural element to continue.");
      return;
    }
  
    setLoading(true);
    
    // Build selectedContent from selectedElements and generatedElements
    const selectedContent = {};
    Object.keys(selectedElements).forEach((key) => {
      if (selectedElements[key]) {
        selectedContent[key] = generatedElements[key];
      }
    });
    
    const lesson_plan_id = generatedElements["id"];
    track('Clicked Generate Lesson Plan', { lesson_plan_id, screen_name: 'Select Elements' });
    const requestBody = {
      grade: selectedGrade,
      standard: selectedStandardIdentifier,
      standardDescription: selectedStandardDescription,
      concept: selectedConcept,
      selected_elements: selectedContent,
      id: lesson_plan_id,
      language: selectedLanguage,
    };
    
    try {
      console.log("Request Body:", requestBody);
      const token = localStorage.getItem('authToken');
      const response = await fetch(`/api/generate_lesson_plan/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token && { 'Authorization': `Token ${token}` }),
        },
        body: JSON.stringify(requestBody),
      });
      
      if (!response.ok) {
        throw new Error("Failed to generate lesson plan.");
      }
      
      const responseData = await response.json();
      console.log("Response Data:", responseData);
      console.log("Lesson Plan ID:", responseData.id);
      track('Viewed Display Lesson Plan', { lesson_plan_id: responseData.id, screen_name: 'Select Elements' });
      invalidateDashboardPlans(token);
      navigate(`/display-lesson-plan?id=${isResuming ? resumeData?.id : responseData.id}`, {
        state: {
          lessonPlan: responseData.lesson_plan,
          title: responseData.title || resumeData?.title || 'Untitled Lesson Plan',
          originalSelections: {
            subject: selectedSubject,
            grade: selectedGrade,
            concept: selectedConcept,
            subconcept: selectedSubconcept,
            standard: selectedStandardIdentifier,
            standardDescription: selectedStandardDescription,
            language: selectedLanguage,
            moku: selectedMoku,
            island: selectedIsland,
            files: incomingFiles,
            fileTypes,
            selectedElements,
          },
          resumeData,
          isResuming,
        },
      });
    } catch (error) {
      console.error("Failed to generate lesson plan:", error);
      alert("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };
  
  

  return (
    <>
      {loading && (
        <div className={styles.loadingOverlay}>
          <div className={styles.loadingModal}>
            <p className={styles.loadingHeadline}>
              Building your culturally-responsive lesson…
            </p>
            <img
              src="/assets/KumuConnectLoading2.gif"
              alt="Loading..."
              className={styles.loaderImage}
            />
            <p className={styles.loadingBody}>
              We&apos;re taking our time to blend your selected cultural references
              with STEM curricula and vocabulary in one thoughtful generation. While
              this takes longer than rapid-fire AI responses, it means fewer
              computational attempts and a more efficient, environmentally-conscious
              process.
            </p>
          </div>
        </div>
      )}
      <div className={`app-container ${styles.openSansScope}`}>
        <NavBar />
        <div className={styles.page}>
          <div className={styles.mainContent}>
            <LessonCreatorSidebar
                currentStep="connections"
                hideToolsButton
                onStepClick={(stepId) => {
                  if (stepId === 'lessonInfo') {
                    navigate('/create-lesson-plan/form', {
                      state: {
                        ...location.state,
                        selectedElements,
                        generatedElements: generatedElements || location.state?.generatedElements,
                      },
                    });
                  }
                }}
              />
            <div className={styles.mainForm}>
              <ElementSelector
                elements={generatedElements || {}}
                grade={selectedGrade}
                standard={selectedStandardIdentifier}
                language={selectedLanguage}
                moku={selectedMoku}
                island={selectedIsland}
                onSelectionChange={handleSelectionChange}
                onContentChange={setGeneratedElements}
                onNext={handleNext}
              />
            </div>
          </div>
          <div className={styles.feedbackButtonContainer}>
            <button
              type="button"
              className={styles.feedbackBtn}
              onClick={handleFeedback}
              aria-label={t('feedback', language)}
            >
              <img src="/assets/light_bulb.svg" alt="" className={styles.feedbackBtnIcon} />
              <span>{t('feedback', language)}</span>
            </button>
          </div>
          <div className={styles.bottomStrip} aria-hidden />
        </div>
      </div>
    </>
  );
}

function SelectElements() {
  return (
    <ProtectedRoute>
      <SelectElementsContent />
    </ProtectedRoute>
  );
}

export default SelectElements;
